import * as React from "react";
import { Box, Stack, Typography, Button } from "@mui/material";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import GroupRoundedIcon from "@mui/icons-material/GroupRounded";
import SatelliteAltRoundedIcon from "@mui/icons-material/SatelliteAltRounded";
import MemoryRoundedIcon from "@mui/icons-material/MemoryRounded";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";

const NAV_ITEMS = [
  { page: "AdminWorkshops", label: "Workshops", icon: EventRoundedIcon },
  { page: "AdminUsers", label: "Participants", icon: GroupRoundedIcon },
  { page: "AdminMissions", label: "Missions", icon: SatelliteAltRoundedIcon },
  { page: "AdminPayloads", label: "Payloads", icon: MemoryRoundedIcon },
  { page: "AdminLaunches", label: "Launches", icon: RocketLaunchRoundedIcon },
];

// Solid fills, no backdropFilter/blur anywhere in the admin area -- a blur
// over the busy background photo is what was washing out Select dropdowns
// and other fields to the point of being unreadable.
export const fieldSx = {
  "& .MuiOutlinedInput-root": {
    color: "#1F2430",
    fontSize: 13.5,
    borderRadius: 1.5,
    bgcolor: "#FFFFFF",
    "& fieldset": { borderColor: "#D8DAE3" },
    "&:hover fieldset": { borderColor: "#B7BBCB" },
    "&.Mui-focused fieldset": { borderColor: "#7C3AED" },
  },
  "& .MuiInputLabel-root": { color: "#4B5563", fontSize: 13, fontWeight: 600 },
  "& .MuiInputLabel-root.Mui-focused": { color: "#7C3AED" },
  // A shrunk label straddles the outline: its top half sits over whatever is
  // *behind* the field (this panel's dark bg), its bottom half over the
  // field's own white bg -- without an explicit background the top half is
  // dark-on-dark and unreadable. Pin a solid white pill behind the whole
  // label so it's legible on both halves.
  "& .MuiInputLabel-root.MuiInputLabel-shrink": {
    backgroundColor: "#FFFFFF",
    px: 0.6,
    borderRadius: 0.5,
  },
  "& .MuiSvgIcon-root": { color: "#6B7280" },
};

export const selectMenuProps = { PaperProps: { sx: { bgcolor: "#FFFFFF", color: "#1F2430" } } };

// fieldSx targets "& .MuiOutlinedInput-root" as a *descendant* -- that's
// correct for TextField (its root wraps a child with that class) but a bare
// <Select> IS its own MuiOutlinedInput-root, so fieldSx never matches and it
// falls back to a transparent/dark background. Use this on standalone
// <Select> components instead.
export const selectFieldSx = {
  color: "#1F2430",
  fontSize: 13.5,
  borderRadius: 1.5,
  bgcolor: "#FFFFFF",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#D8DAE3" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#B7BBCB" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#7C3AED" },
  "& .MuiSvgIcon-root": { color: "#6B7280" },
};

export const panelSx = {
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.75 },
  border: "1px solid #3E4258",
  bgcolor: "#2B2E40",
};

export function primaryButtonSx(overrides: object = {}) {
  return {
    bgcolor: "#7C3AED",
    "&:hover": { bgcolor: "#6D28D9" },
    textTransform: "none",
    fontWeight: 800,
    borderRadius: 1.5,
    ...overrides,
  };
}

// The per-tile "Manage" / "Close" toggle shared by the Workshops and Missions tabs.
export function manageButtonSx(open: boolean) {
  return {
    textTransform: "none",
    fontWeight: 800,
    fontSize: 14,
    px: 2,
    py: 0.75,
    borderRadius: 1.5,
    color: "#E9D5FF",
    borderColor: "#7C3AED",
    bgcolor: open ? "transparent" : "rgba(124,58,237,0.18)",
    "&:hover": { borderColor: "#A78BFA", bgcolor: "rgba(124,58,237,0.3)" },
  };
}

// Header row of a tile: clicking it opens the read-only details.
export const tileHeaderSx = {
  cursor: "pointer",
  borderRadius: 1.5,
  mx: -1,
  px: 1,
  my: -0.75,
  py: 0.75,
  "&:hover": { bgcolor: "rgba(255,255,255,0.04)" },
};

export const infoBlockSx = {
  borderLeft: "3px solid #7C3AED",
  bgcolor: "#241B3B",
  borderRadius: 1.5,
  p: 1.75,
};

export const sectionTitleSx = { color: "#FFFFFF", fontWeight: 800, fontSize: 13.5 };
export const mutedTextSx = { color: "rgba(226,232,240,0.5)", fontSize: 12.5 };

export default function AdminShell(props: {
  title: string;
  currentPage: string;
  handlePageChange: (page: string) => void;
  children: React.ReactNode;
}) {
  const { title, currentPage, handlePageChange, children } = props;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(160deg, #362654 0%, #2A2740 35%, #33384A 70%, #3A3F52 100%)",
        px: { xs: 2, sm: 3, md: 5 },
        pt: { xs: 9, sm: 9, md: 7 },
        pb: { xs: 4, sm: 5, md: 6 },
      }}
    >
      <Stack spacing={3} sx={{ width: "100%", maxWidth: 1100, mx: "auto" }}>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap rowGap={1}>
          <Typography
            sx={{
              color: "#D1D5DB",
              fontWeight: 800,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              fontSize: 11,
              mr: 1,
            }}
          >
            Admin
          </Typography>

          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const selected = currentPage === item.page;
            return (
              <Button
                key={item.page}
                onClick={() => handlePageChange(item.page)}
                startIcon={<Icon sx={{ fontSize: 17 }} />}
                sx={{
                  textTransform: "none",
                  fontWeight: 800,
                  fontSize: 13,
                  borderRadius: 999,
                  px: 1.75,
                  color: selected ? "#FFFFFF" : "#D1D5DB",
                  bgcolor: selected ? "#7C3AED" : "#2B2E40",
                  border: "1px solid",
                  borderColor: selected ? "#7C3AED" : "#3E4258",
                  "&:hover": { bgcolor: selected ? "#6D28D9" : "#363A4F" },
                }}
              >
                {item.label}
              </Button>
            );
          })}
        </Stack>

        <Typography sx={{ color: "#FFFFFF", fontWeight: 900, fontSize: { xs: 24, sm: 28 } }}>{title}</Typography>

        {children}
      </Stack>
    </Box>
  );
}
